import React, { useState } from 'react';
import { apiGet, apiPost, apiUpload } from '../api';

const CartPage = ({ cart, setCart, onBack, onOrderPlaced }) => {
  const [preorderOnly, setPreorderOnly] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [qrUrl, setQrUrl] = useState('');
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '', city: '', pincode: '', notes: '' });
  const [proofUploading, setProofUploading] = useState(false);
  const [paymentProofUrl, setPaymentProofUrl] = useState('');

  // Calculate total cart value
  const calculateTotal = () => {
    return cart.reduce((total, item) => {
      const price = parseFloat((item.book.price || item.book.rate || '0').replace(/[^0-9.]/g, ''));
      return total + (price * item.quantity);
    }, 0);
  };

  const changeQty = (id, q) => setCart((prev) => prev.map((p) => (p.book.id === id ? { ...p, quantity: Math.max(1, q) } : p)));
  const removeFromCart = (id) => setCart((prev) => prev.filter((p) => p.book.id !== id));

  const placeOrder = async (e) => {
    e.preventDefault();
    if (!cart.length) return;
    setPlacing(true);
    setOrderResult(null);
    try {
      // Basic client-side validation to reduce 400s
      const name = String(form.name || '').trim();
      const phone = String(form.phone || '').trim();
      const address = String(form.address || '').trim();
      if (!name || phone.length < 7 || address.length < 5) {
        alert('Please enter a valid Name, Phone (min 7 digits), and Address (min 5 characters).');
        return;
      }
      if (!preorderOnly && !paymentProofUrl) {
        alert('Please upload a payment screenshot (required unless this is a preorder).');
        return;
      }
      const payload = {
        name,
        email: form.email || undefined,
        phone,
        address,
        city: form.city || undefined,
        pincode: form.pincode || undefined,
        notes: form.notes || undefined,
        paymentProofUrl: paymentProofUrl || undefined,
        preorder: preorderOnly,
        items: cart.map((c) => ({ bookId: Number(c.book.id), quantity: Math.max(1, parseInt(c.quantity, 10) || 1) })),
      };
      const res = await apiPost('/api/orders', payload);
      setOrderResult(res);
      setCart([]);
      if (typeof onOrderPlaced === 'function') onOrderPlaced();
      try {
        const qr = await apiGet('/api/payment/qr');
        setQrUrl(qr?.url || '');
      } catch (_) {}
    } catch (err) {
      console.error('Order placement error:', err);
      const msg = err?.response?.data?.error || err?.message || 'Failed to place order. Please check your details and try again.';
      alert(msg);
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', padding: '20px 0', paddingRight: '4rem' }}>
      <div className="container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0 }}>Shopping Cart {preorderOnly ? '(Preorder)' : ''}</h2>
          <button className="btn btn-secondary" onClick={onBack}>← Back to Store</button>
        </div>

        {cart.length === 0 ? (
          <div className="card" style={{ padding: 24, textAlign: 'center' }}>
            <p style={{ color: '#666', fontSize: 18 }}>Your cart is empty</p>
            <button className="btn btn-primary" onClick={onBack}>Continue Shopping</button>
          </div>
        ) : (
          <>
            <div className="card" style={{ padding: 16, marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ margin: 0 }}>Cart Items</h3>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#2d5016' }}>
                  Total: ₹{calculateTotal().toFixed(2)}
                </div>
              </div>
              <div style={{ display: 'grid', gap: 12 }}>
                {cart.map((it) => (
                  <div key={it.book.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, border: '1px solid #e0e0e0', borderRadius: 6 }}>
                    <img src={it.book.coverUrl || it.book.cover} alt={it.book.name} style={{ width: 60, height: 80, objectFit: 'cover', borderRadius: 4 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>{it.book.name}</div>
                      <div style={{ color: '#2d5016', fontWeight: 600 }}>{it.book.price || it.book.rate}</div>
                    </div>
                    <input className="input" type="number" min={1} value={it.quantity} onChange={(e) => changeQty(it.book.id, Number(e.target.value))} style={{ width: 80 }} />
                    <button className="btn btn-secondary" onClick={() => removeFromCart(it.book.id)}>Remove</button>
                  </div>
                ))}
              </div>
            </div>

            <div className="card" style={{ padding: 16 }}>
              <h3 style={{ marginTop: 0 }}>Checkout</h3>
              
              {/* QR Code for Payment */}
              {!preorderOnly && (
                <div style={{ marginBottom: 20, padding: 16, background: '#f0f8f0', borderRadius: 8, textAlign: 'center' }}>
                  <h4 style={{ marginTop: 0, color: '#2d5016' }}>Scan to Pay</h4>
                  <img src="/QR/QRCode.jpeg" alt="Payment QR Code" style={{ maxWidth: 300, width: '100%', border: '2px solid #2d5016', borderRadius: 8, marginBottom: 12 }} />
                  <p style={{ margin: 0, color: '#666', fontSize: 14 }}>Scan this QR code to complete your payment</p>
                </div>
              )}
              
              <form className="form" onSubmit={placeOrder}>
                <div className="form-grid-2">
                  <div className="form-group"><label>Name *</label><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
                  <div className="form-group"><label>Phone *</label><input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required /></div>
                  <div className="form-group"><label>Email</label><input className="input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                  <div className="form-group"><label>City</label><input className="input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
                  <div className="form-group"><label>Pincode</label><input className="input" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} /></div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}><label>Address *</label><textarea className="input" rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} required /></div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}><label>Notes</label><textarea className="input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Add payment reference number or any special instructions (optional)" /></div>
                  
                  {/* Payment Screenshot Upload */}
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Payment Screenshot {preorderOnly ? '(optional for preorder)' : '*(required)'}</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <input
                        type="file"
                        accept="image/*"
                        required={!preorderOnly && !paymentProofUrl}
                        onChange={async (e) => {
                          const file = e.target.files && e.target.files[0];
                          if (!file) return;
                          try {
                            setProofUploading(true);
                            const res = await apiUpload('/api/upload-public', file);
                            setPaymentProofUrl(res?.url || '');
                          } catch (err) {
                            alert('Failed to upload image. Please try a smaller JPG/PNG/WEBP.');
                          } finally {
                            setProofUploading(false);
                          }
                        }}
                      />
                      {proofUploading && <span style={{ color: '#666' }}>Uploading…</span>}
                      {paymentProofUrl && (
                        <a href={paymentProofUrl} target="_blank" rel="noreferrer" style={{ color: '#2d5016' }}>
                          View uploaded image
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="checkbox" checked={preorderOnly} onChange={(e) => setPreorderOnly(e.target.checked)} />
                    <span>This is a preorder (no payment required)</span>
                  </label>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button className="btn btn-primary" type="submit" disabled={placing || cart.length === 0}>{placing ? 'Placing Order…' : 'Place Order'}</button>
                </div>
              </form>

              {orderResult && (
                <div className="card" style={{ marginTop: 16, padding: 16, background: '#f0f8f0' }}>
                  <h4 style={{ marginTop: 0, color: '#2d5016' }}>✓ Order Placed Successfully</h4>
                  <p style={{ margin: '8px 0' }}>Order ID: <strong>#{orderResult.id}</strong></p>
                  {qrUrl ? (
                    <div>
                      <p style={{ marginBottom: 8 }}>Scan this QR code to complete payment:</p>
                      <img src={qrUrl} alt="Payment QR" style={{ maxWidth: 240, border: '2px solid #2d5016', borderRadius: 8 }} />
                    </div>
                  ) : (
                    <p style={{ color: '#666' }}>Payment QR code is currently unavailable. We will contact you for payment details.</p>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CartPage;
